import { Component, Input } from '@angular/core';

import { State } from './state.model';
import { PromiseState, StatefulPromise } from '../../util/stateful-promise';


/**
 * Function type that maps a StatefulPromise to the component state
 */
export type MapStateFunction = (promise: StatefulPromise<any>) => State;


/**
 * Default state mapper function (maps promises that are fulfilled with something other than null or undefined to the
 * Content state).
 * @param {StatefulPromise<any>} promise Stateful promise
 * @returns {State} Component state
 */
export const DEFAULT_STATE_MAPPER: MapStateFunction = (promise: StatefulPromise<any>) => {
  if (!promise) {
    return State.Empty;
  }
  switch (promise.state) {
    case PromiseState.Pending:
      return State.Loading;
    case PromiseState.Fulfilled:
      return (promise.value === null || promise.value === undefined) ? State.Empty : State.Content;
    case PromiseState.Rejected:
      return State.Error;
  }
};


/**
 * Wrapper component that has empty, loading, content, and error states. Only the proper child view will be shown
 * depending on the state. The component may be given a state directly, or a StatefulPromise to track.
 */
@Component({
  selector: 'app-state-view',
  templateUrl: './state-view.component.html',
  styleUrls: ['./state-view.component.scss']
})
export class StateViewComponent {
  private _promise: StatefulPromise<any>;
  State = State;

  /**
   * Component state. When this property is updated, the view will be updated to reflect the new state.
   */
  @Input() state: State = State.Empty;

  /**
   * Stateful promise from which the state will be derived. This may be used in lieu of the "state" property. When
   * the promise is fulfilled or rejected, the component state will be updated based on the promise state.
   */
  @Input() set promise(promise: StatefulPromise<any>) {
    this._promise = promise;
    this._updateState();
    if (promise && promise.state === PromiseState.Pending) {
      this._promise.promise.then(() => this._updateState(), () => this._updateState());
    }
  }

  /**
   * Optional state mapping function. By default, the following logic is used:
   * (1) If the promise is undefined, it will be mapped to the "Empty" state.
   * (2) If the promise is not fulfilled (neither resolved nor rejected), it will be mapped to the "Pending" state.
   * (3) If the promise resolves to a value other than null or undefined, it will be mapped to the "Content" state.
   * (4) If the promise resolves to either null or undefined, it will be mapped to the "Empty" state.
   * (5) If the promise is rejected, it will be mapped to the "Error" state.
   */
  @Input() mapState: MapStateFunction = DEFAULT_STATE_MAPPER;

  private _updateState() {
    this.state = this.mapState(this._promise);
  }
}
